/* istanbul ignore file */
// TODO: move test from useStyliticsConfiguration to its own file

import { ref } from 'vue';
import { useMutationObserver } from '@vueuse/core';
import { getHeadingText, getStyliticsItemNumber } from '../utils/WidgetUtil';
import { getOutfitsAvailabilityStatus } from '../utils/RequestUtil';
import { SET_CTA_JUMP_CLICK, SET_OUTFIT_AVAILIBILITY } from '../types/setters';

export const useOutfitting = () => {
  /**
   * Outfitting Anchor CTA logic
   * @param {object} CtaItemParams
   * @returns
   */
  const pdpCtaItemData = ({
    brandFlags,
    featureFlags,
    fireLinkTag,
  }, store) => {
    // Set aliases to properties to avoid conflict with the already set variables on top level
    const { isBeautyProduct: isBeauty, isHomeFOB: isHome } = featureFlags;

    const brandName = brandFlags.isMcom ? 'mcom' : 'bcom';

    const headingText = getHeadingText({
      brand: ref(brandName),
      pageType: ref('pdp'),
      isBeautyProduct: ref(isBeauty),
      isHomeProduct: ref(isHome),
    });

    /**
     * Beauty should show PAIR IT WITH and a line break element
     * must be added to FIRST found space for design compliance
     */
    const ctaText = isBeauty && headingText.toLowerCase() === 'pair it with' ? headingText.replace(/ /, '<br>') : headingText;

    // Set new Stylitics event
    const fireStyliticsJumplinkEvent = new Event('fireStyliticsJumplinkEvent');

    const observeAndScroll = (initialPosition, elOutfittingSectionAnchor) => {
      const el = document.getElementById('app-wrapper');
      const { stop } = useMutationObserver(
        el,
        (mutations) => {
          const mutation = mutations[0];

          if (!mutation) return;

          let initPos = initialPosition;
          const newPosition = elOutfittingSectionAnchor.offsetTop;
          if (newPosition !== initPos) {
            window.scrollTo({
              top: newPosition - 100,
              behavior: 'smooth',
            });
            initPos = newPosition;
          }
        },
        { childList: true, subtree: true },
      );

      setTimeout(() => {
        stop();
      }, 5000);
    };

    return {
      customCssClass: 'font-big',
      id: 'outfitting',
      label: ctaText,
      onClickHandler: () => {
        // Scroll down to Outfitting section
        const elOutfittingSectionAnchor = document.querySelector('[name="outfitting-section-anchor"]');

        const initialPosition = elOutfittingSectionAnchor?.offsetTop;
        if (elOutfittingSectionAnchor) {
          window.scrollTo({
            // (-100) help keep widget in-view even when it hasn't been loaded
            top: initialPosition - 100,
            left: 0,
            behavior: 'smooth',
          });
        }

        observeAndScroll(initialPosition, elOutfittingSectionAnchor);

        const utagData = {
          bcom: { event_name: 'wear it with anchor' },
          mcom: { page_type: 'product', link_name: 'wear_it_with_click' },
        };

        fireLinkTag(utagData[brandName]);

        // Set Stylitics cta jump click property so when widget is mounted
        // it we can trigger Stylitics tag events base off the instance.
        // As of  right here, we do not have access to it.
        window.dispatchEvent(fireStyliticsJumplinkEvent);
        store.commit(`outfittingStore/${SET_CTA_JUMP_CLICK}`, true);
      },
      showArrow: isBeauty,
    };
  };

  // const worker = ref(null);
  // // TODO: Can be a global util in some library..
  // /**
  //  * @param {boolean} flag
  //  * @return {boolean}
  //  */
  // const checkWebWorkerInit = (flag) => typeof Worker !== 'undefined' && flag;
  // // If browser supports webWorkers and application wants to use it

  // const pdpOutfitAvailabilityCheckWithWebWorker = async (args, workerParam, store) => new Promise((resolve, reject) => {
  //   workerParam.onmessage = async (e) => {
  //     resolve(true);
  //     store.commit(`outfittingStore/${SET_OUTFIT_AVAILIBILITY}`, e.data);
  //   };
  //   workerParam.onerror = async () => {
  //     reject(new Error('outfitting web worker error'));
  //   };

  //   workerParam.postMessage(args);
  // });

  /**
   * Call stylitics API for the given item number to check if
   * there are outfits for it or not.
   *
   * @param {object} OutfitAvailabilityCheckParams
   * @returns {bool}
   */
  const pdpOutfitAvailabilityCheck = async ({
    productData,
    // Set alias to selectedBrand to avoid conflict with the already set variable on top level
    brand: selectedBrand,
    // Set alias to selectedColor to avoid conflict with the already set variable on top level
    selectedColorName: selectedColor,
    featureFlags,
  }, store) => {
    // if (worker.value) {
    //   const nonReactiveValue = {
    //     productData: productData?.value,
    //     selectedBrand,
    //     selectedColor,
    //     featureFlags,
    //   };
    //   try {
    //     await pdpOutfitAvailabilityCheckWithWebWorker(
    //       JSON.stringify(nonReactiveValue),
    //       worker.value,
    //       store,
    //     );
    //   } catch (err) {
    //     // eslint-disable-next-line
    //     console.warn('Outfitting web worker failed');
    //   } finally {
    //     worker.value?.terminate();
    //     worker.value = null;
    //   }
    //   return;
    // }
    try {
      const itemNumber = getStyliticsItemNumber({
        product: productData,
        brand: ref(selectedBrand),
        // Color is not required since the intention is to get all outfits for all color combinations
        selectedColorName: ref(selectedColor),
        optionals: {
          isMasterProduct: ref(featureFlags.isMasterProduct),
          isGetAllOutfits: ref(true),
          isConsolidatedPage: ref(featureFlags.isConsolidatedPage),
          isConsolidatedThirdVariant: ref(featureFlags.isConsolidatedThirdVariant),
        },
      });

      /**
       * A successful response is always an array of outfits for the
       * provided product.
       */
      const hasOutfits = await getOutfitsAvailabilityStatus({
        brand: selectedBrand,
        featureFlags,
        itemNumber,
        productId: productData.value?.id,
      });

      store.commit(`outfittingStore/${SET_OUTFIT_AVAILIBILITY}`, hasOutfits);
    } catch (err) {
      // eslint-disable-next-line
      console.warn('Outfitting: Unable to run outfits availability check');
    }
  };

  // onMounted(() => {
  //   if (checkWebWorkerInit(initWebWorker)) {
  //     worker.value = new Worker(
  //       new URL('../web_workers/outfittingCta.worker.js', import.meta.url),
  //       { type: 'module' },
  //     );
  //   }
  // });

  return {
    pdpCtaItemData,
    pdpOutfitAvailabilityCheck,
  };
};

export default useOutfitting;
